import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/home";

const filterTabs = ["全部", ...categories.map((c) => c.name)];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "事件合集 - 蝌蚪黑料",
  description: "按综艺、直播、影视、演出等板块浏览全网热门事件合集。",
  url: "https://bhmbk.cn/collections",
};

export default function Collections() {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") ?? "全部";
  const initialQuery = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQuery);

  const list = useMemo(() => {
    const kw = keyword.trim();
    return topics.filter((t) => {
      const matchCategory = activeCategory === "全部" || t.category === activeCategory;
      const matchKeyword =
        !kw ||
        t.title.includes(kw) ||
        t.summary.includes(kw) ||
        t.tags.some((tag) => tag.includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const setCategory = (name: string) => {
    const next = new URLSearchParams(params);
    if (name === "全部") next.delete("category");
    else next.set("category", name);
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navbar />
      <main className="flex-1 pt-16 md:pt-[72px]">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-10 md:py-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "事件合集" }]} />
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-foreground-950">
              事件合集
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              把散落的消息按话题归拢成一个合集：起因、关键进展、后续更新，一条时间线看完。当前共收录 {topics.length} 个正在追踪的话题。
            </p>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3 max-w-2xl">
              <div className="flex items-center gap-2 h-11 px-4 rounded-full bg-background-50 border border-background-200 focus-within:border-primary-300 flex-1">
                <i className="ri-search-line text-foreground-500"></i>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索事件标题、话题或标签"
                  className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
                />
                {keyword && (
                  <button
                    onClick={() => setKeyword("")}
                    aria-label="清空搜索"
                    className="text-foreground-400 hover:text-foreground-700"
                  >
                    <i className="ri-close-circle-line"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-10 md:py-14">
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {filterTabs.map((tab) => {
                const active = tab === activeCategory;
                return (
                  <button
                    key={tab}
                    onClick={() => setCategory(tab)}
                    className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>

            <p className="text-sm text-foreground-500 mb-6">
              共找到 <span className="text-primary-600 font-medium">{list.length}</span> 个合集
              {activeCategory !== "全部" && ` · 板块「${activeCategory}」`}
            </p>

            {list.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {list.map((t) => (
                  <TopicCard key={t.id} topic={t} />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200">
                <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <p className="text-foreground-800">没有找到匹配的合集</p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词或切换板块再试试</p>
                <button
                  onClick={() => {
                    setKeyword("");
                    setCategory("全部");
                  }}
                  className="mt-5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
                >
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}